import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, ChevronRight } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantCoachPanel from "../components/applicant/ApplicantCoachPanel";
import { useApplicantCoach } from "../components/applicant/ApplicantCoachContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import config from "../config/config";
import { buildAuthFetchOptions, clearStoredAuth } from "../utils/authSession";

async function fetchJson(path) {
  const response = await fetch(`${config.API_BASE_URL}${path}`, buildAuthFetchOptions());
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

function formatDate(value) {
  if (!value) return "gerade";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", { dateStyle: "medium" });
}

export default function ApplicantMatches() {
  const navigate = useNavigate();
  const { registerCoachSurface } = useApplicantCoach();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  useEffect(() => {
    document.title = "Experts4Europe | Matches";
  }, []);

  const handleAuthError = (err) => {
    if (Number(err?.status) !== 401) return false;
    clearStoredAuth();
    const detail = String(err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchJson("/api/applicant-dashboard/mission-control");
        if (mounted) setSnapshot(data);
      } catch (err) {
        if (handleAuthError(err)) return;
        if (mounted) setError(err.message || "Die Chancen konnten nicht geladen werden.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const opportunities = snapshot?.opportunities || [];
  const featuredOpportunity = opportunities[0] || null;
  const otherOpportunities = opportunities.slice(1);
  const coachSurfaceContext = useMemo(() => {
    const featuredReasons = Array.isArray(featuredOpportunity?.why_relevant)
      ? featuredOpportunity.why_relevant.slice(0, 3)
      : [];
    const featuredMissingItems = Array.isArray(featuredOpportunity?.missing_items)
      ? featuredOpportunity.missing_items.slice(0, 3)
      : [];
    const coachNextStep = featuredOpportunity
      ? {
          key: `matches_${featuredOpportunity.job_id || "featured"}`,
          label_de: featuredOpportunity.next_step_label || "Chance ansehen",
          label_en: "review this opportunity",
          reason_de: featuredMissingItems[0]?.reason || featuredReasons[0] || "Diese Chance ist aktuell am relevantesten.",
          href: featuredOpportunity.next_step_href || featuredOpportunity.href || "/dashboard/applicant/matches",
        }
      : {
          key: "matches_wait",
          label_de: "Dashboard oeffnen",
          label_en: "open your dashboard",
          reason_de: "Gerade ist keine neue sichtbare Chance vorhanden.",
          href: "/dashboard/applicant",
        };
    const coachMissingItems = featuredMissingItems.length
      ? featuredMissingItems.map((item, index) => ({
          key: `matches_missing_${featuredOpportunity?.job_id || "featured"}_${index}`,
          label_de: item.label || "Offener Punkt",
          label_en: "open point",
          reason_de: item.reason || item.label || "Hier ist noch etwas offen.",
          href: item.href || featuredOpportunity?.href || "/dashboard/applicant/matches",
        }))
      : featuredOpportunity
        ? [{
            key: `matches_priority_${featuredOpportunity.job_id || "featured"}`,
            label_de: featuredOpportunity.title || "Chance",
            label_en: "opportunity",
            reason_de: featuredReasons[0] || "Diese Chance ist aktuell die sichtbar wichtigste.",
            href: featuredOpportunity.href || "/dashboard/applicant/matches",
          }]
        : [];

    return {
      screen_key: "applicant_matches",
      screen_label: "Chancen",
      highlighted_step: `${coachNextStep.label_de}: ${coachNextStep.reason_de}`,
      surface_state_key: `matches:${loading ? "loading" : opportunities.length}:${featuredOpportunity?.job_id || "none"}:${coachMissingItems.length}:${error ? "error" : "ok"}`,
      focused_opportunity_id: featuredOpportunity?.job_id || null,
      surface_summary: {
        surface_type: "matches",
        status_label: loading
          ? "Chancen werden geladen"
          : opportunities.length
            ? `${opportunities.length} sichtbare Chancen`
            : "Keine neue Chance sichtbar",
        status_note: loading
          ? "Die sichtbaren Chancen werden gerade geladen."
          : featuredOpportunity
            ? `${featuredOpportunity.title} ist aktuell die wichtigste sichtbare Chance.`
            : "Gerade ist keine neue Chance ausserhalb deiner laufenden Bewerbungen sichtbar.",
        visible_opportunity_count: opportunities.length,
        featured_opportunity: featuredOpportunity ? {
          job_id: featuredOpportunity.job_id || 0,
          title: featuredOpportunity.title || "",
          fit_band: featuredOpportunity.fit_band || "",
          fit_score: featuredOpportunity.fit_score || 0,
          region_label: featuredOpportunity.region_label || "",
          why_relevant: featuredReasons,
          missing_items: featuredMissingItems.map((item) => ({
            label_de: item.label || "Offener Punkt",
            reason_de: item.reason || item.label || "",
            href: item.href || featuredOpportunity.href || "/dashboard/applicant/matches",
          })),
        } : null,
        visible_opportunities: opportunities.slice(0, 3).map((item) => ({
          job_id: item.job_id || 0,
          title: item.title || "",
          fit_band: item.fit_band || "",
          fit_score: item.fit_score || 0,
          region_label: item.region_label || "",
        })),
        coach_next_step: coachNextStep,
        coach_missing_items: coachMissingItems.slice(0, 4),
      },
    };
  }, [error, featuredOpportunity, loading, opportunities]);

  useEffect(() => {
    registerCoachSurface(coachSurfaceContext);
  }, [coachSurfaceContext, registerCoachSurface]);
  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Zur Mission Control" },
    { href: "/dashboard/applicant/matches", label: "Matches", description: "Deine neuen Chancen" },
  ]), []);

  return (
    <ApplicantShell
      title="Matches"
      subtitle="Hier pruefst du nur neue Chancen und ihre Passung zu deinem Profil."
      actions={(
        <>
          <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/dashboard/applicant")}>Dashboard</Button>
          <Button className="applicant-cta-button" onClick={() => navigate("/stb/job-marketplace")}>Zum Marktplatz</Button>
        </>
      )}
      notifications={[]}
      searchItems={searchItems}
    >
      <div className="applicant-workspace-page">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        <section className="applicant-workspace-hero applicant-workspace-hero--matches">
          <div className="applicant-workspace-hero__main">
            <span className="applicant-section-kicker">Neue Chancen</span>
            <h2>Welche Stellen jetzt gut zu dir passen</h2>
            <p>Du siehst hier neue Jobs, die zu deinem Profil, deinem Ort und deinem aktuellen Stand passen.</p>
          </div>
        </section>

        <div className="applicant-workspace-grid applicant-workspace-grid--main">
          <section className="applicant-workspace-card">
            <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Lead-Chance</span><h3>Deine staerkste neue Chance</h3></div></div>
            {loading ? <div className="applicant-empty-card">Chancen werden geladen ...</div> : featuredOpportunity ? (
              <article className="applicant-opportunity-feature">
                <div className="applicant-opportunity-feature__meta"><span className="applicant-opportunity-card__fit">{featuredOpportunity.fit_band}</span><small>{formatDate(featuredOpportunity.released_at)}</small></div>
                <strong>{featuredOpportunity.title}</strong>
                <p>{featuredOpportunity.employer_name} · {featuredOpportunity.region_label}</p>
                <div className="applicant-opportunity-feature__stats">
                  <div><span>Fit</span><strong>{featuredOpportunity.fit_score}</strong></div>
                  <div><span>Beruf</span><strong>{featuredOpportunity.profession || "Noch offen"}</strong></div>
                </div>
                <div className="applicant-workspace-grid applicant-workspace-grid--double">
                  <div className="applicant-detail-card">
                    <span className="applicant-section-kicker">Warum sie passt</span>
                    {(featuredOpportunity.why_relevant || []).length ? (
                      <div className="applicant-checklist">
                        {featuredOpportunity.why_relevant.slice(0, 3).map((item) => (
                          <div key={item} className="applicant-checklist__item is-done">
                            <BadgeCheck size={16} />
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>Diese Chance wirkt aktuell besonders passend fuer dein sichtbares Profil.</p>
                    )}
                  </div>
                  <div className="applicant-detail-card">
                    <span className="applicant-section-kicker">Was noch offen ist</span>
                    {(featuredOpportunity.missing_items || []).length ? (
                      <div className="applicant-checklist">
                        {featuredOpportunity.missing_items.slice(0, 3).map((item) => (
                          <div key={`${item.label}-${item.href}`} className="applicant-checklist__item is-open">
                            <ChevronRight size={16} />
                            <span>{item.reason || item.label}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>Aus der sichtbaren Flaeche heraus bist du fuer diese Chance grundsaetzlich bereit.</p>
                    )}
                  </div>
                </div>
                <button type="button" className="applicant-inline-link" onClick={() => navigate(featuredOpportunity.href)}><span>Chance oeffnen</span><ChevronRight size={15} /></button>
              </article>
            ) : <div className="applicant-empty-card">Aktuell liegt keine neue Chance ausserhalb deiner laufenden Bewerbungen offen.</div>}
          </section>

          <section className="applicant-workspace-card">
            <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Mehr Chancen</span><h3>Weitere passende Stellen</h3></div></div>
            <div className="applicant-opportunity-stack">
              {otherOpportunities.length ? otherOpportunities.map((item) => (
                <article key={item.job_id} className="applicant-opportunity-card">
                  <div className="applicant-opportunity-card__meta"><span className="applicant-opportunity-card__fit">{item.fit_band}</span><small>{formatDate(item.released_at)}</small></div>
                  <strong>{item.title}</strong>
                  <p>{item.employer_name} · {item.region_label}</p>
                  <div className="applicant-opportunity-card__foot"><span>Fit {item.fit_score}</span><button type="button" className="applicant-inline-link" onClick={() => navigate(item.href)}><span>Zur Chance</span><ChevronRight size={15} /></button></div>
                </article>
              )) : <div className="applicant-empty-card">Keine weiteren neuen Chancen sichtbar.</div>}
            </div>
          </section>
        </div>
      </div>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ApplicantCoachPanel />
    </ApplicantShell>
  );
}
